import { logger } from '@/lib/logger'

interface CleanupOptions {
  docTypeHint?: 'book' | 'form' | 'generic'
}

/**
 * Very conservative cleanup for obvious gutter/edge text before normalization.
 *
 * Target pattern (book-like pages such as BR-193ZK4 / BR-7V6EN6):
 * - Long run of many consecutive very short lines (avg length < ~15 chars)
 *   at the start of the page, often a single broken word per line.
 * - Followed by a blank line and then much longer, well‑formed lines
 *   (chapter heading + paragraph text).
 * 
 * We only drop the leading block when:
 * - It has at least MIN_GUTTER_LINES lines, and
 * - Its average length is short while the following block's average length
 *   is significantly longer.
 */
export function cleanupOcrText(rawText: string, options?: CleanupOptions): string {
  if (!rawText.trim()) return rawText

  const docType = options?.docTypeHint || 'generic'
  // Only attempt aggressive gutter cleanup for book-like prose.
  if (docType !== 'book' && docType !== 'generic') {
    return rawText
  }

  // Normalize line endings and strip trailing whitespace. This is safe for
  // book-like and generic prose and helps downstream normalization behave
  // more predictably.
  const normalizedRaw = rawText.replace(/\r\n?/g, '\n')
  const lines = normalizedRaw.split('\n').map((line) => line.replace(/[ \t]+$/g, ''))
  if (lines.length < 10) {
    // Too little structure to make a safe decision.
    return rawText
  }

  // Find first clearly separated block: from start up to first empty line
  let firstBlockEnd = 0
  while (firstBlockEnd < lines.length && lines[firstBlockEnd].trim().length > 0) {
    firstBlockEnd++
  }

  const firstBlock = lines.slice(0, firstBlockEnd)
  const restBlock = lines.slice(firstBlockEnd)

  const MIN_GUTTER_LINES = 12
  if (firstBlock.length < MIN_GUTTER_LINES || restBlock.length === 0) {
    return rawText
  }

  const avgLen = (block: string[]) =>
    block.reduce((sum, l) => sum + l.trim().length, 0) / block.length

  const firstAvg = avgLen(firstBlock)
  const restAvg = avgLen(restBlock.filter((l) => l.trim().length > 0))

  // Heuristic: first block is made of very short lines, second block is much longer.
  const isGutterLike = firstAvg > 0 && firstAvg < 15 && restAvg > firstAvg * 1.8

  if (!isGutterLike) {
    return rawText
  }

  const cleaned = restBlock.join('\n').replace(/^\s*\n+/, '')

  logger.info('OCR cleanup removed leading gutter-like block', {
    removedLines: firstBlock.length,
    firstAvgLen: Math.round(firstAvg),
    restAvgLen: Math.round(restAvg),
  })

  return cleaned.trimStart()
}

